import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Cookies from 'js-cookie';
import mixpanel from 'mixpanel-browser';
import toast from 'react-hot-toast';
import { Pencil, Plus, RefreshCw, Workflow } from 'lucide-react';
import Layout from '../components/layout/AppLayout';
import Header from '../../shared/components/layout/Header';
import Loader from '../../shared/components/ui/Loader';
import FormInput from '../../shared/components/ui/FormInput';
import Modal from '../../shared/components/ui/Modal';
import Button from '../../shared/components/ui/Button';
import WhatsAppFlowMetadataModal from '../components/whatsapp/WhatsAppFlowMetadataModal';
import { useSidebar } from '../../shared/contexts/SidebarContext';
import { useProjectsList } from '../../../contexts/ProjectsContext';
import {
  createWhatsAppFlow,
  getWhatsAppFlows,
  syncWhatsAppFlowTemplateStatus
} from '../services/api/implementation/impl';
import { WHATSAPP_FLOW_VERTICAL_OPTIONS } from '../utils/whatsappFlowUtils';

/** Header with subtitle + filter toolbar — keep offsets in sync */
const FLOW_LIST_HEADER_OFFSET = '4.75rem';
const FLOW_LIST_TOOLBAR_HEIGHT = '3.25rem';
const FLOW_LIST_TOP_CHROME = `calc(${FLOW_LIST_HEADER_OFFSET} + ${FLOW_LIST_TOOLBAR_HEIGHT})`;

const projectFormSchema = yup.object().shape({
  project: yup.string().required('Select Project')
});

const createFlowSchema = yup.object().shape({
  name: yup.string().trim().required('Flow name is required'),
  description: yup.string().trim(),
  vertical: yup.string().trim().required('Select a vertical')
});

function slugifyFlowName(name) {
  const slug = name
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_]/g, '');
  return slug || `flow_${Date.now()}`;
}

function templateApprovalStatusBadgeClass(status) {
  const s = String(status || '').toUpperCase();
  if (s === 'PENDING' || s === 'IN_REVIEW') return 'bg-amber-50 text-amber-800 border-amber-200';
  if (s === 'APPROVED' || s === 'ACTIVE')
    return 'bg-emerald-50 text-emerald-800 border-emerald-200';
  if (s === 'REJECTED' || s === 'DISABLED') return 'bg-rose-50 text-rose-800 border-rose-200';
  return 'bg-gray-50 text-gray-700 border-gray-200';
}

function resolveTemplateApprovalStatus(row) {
  return (
    row?.template_approval_status ??
    row?.template_status ??
    row?.approval_status ??
    ''
  );
}

function mapFlowRow(row) {
  return {
    id: row.id,
    name: row.name || 'Untitled flow',
    description: row.description,
    vertical: row.vertical || '',
    projectId: row.project_id,
    stepCount: Number(row.step_count) || 0,
    templateApprovalStatus: resolveTemplateApprovalStatus(row)
  };
}

function TemplateApprovalBadge({ status }) {
  if (!status) return null;
  return (
    <span
      className={`inline-flex w-fit shrink-0 rounded-full border px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${templateApprovalStatusBadgeClass(
        status
      )}`}>
      {String(status).replace(/_/g, ' ')}
    </span>
  );
}

function FlowCard({ name, description, stepCount, templateApprovalStatus, onClick, onEdit }) {
  return (
    <div className="relative flex min-h-[200px] flex-col rounded-2xl border border-stone-200 bg-white p-5 text-left shadow-sm transition-shadow hover:shadow-md">
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          onEdit?.();
        }}
        aria-label={`Edit ${name}`}
        className="absolute right-4 top-4 z-10 inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 shadow-sm transition-colors hover:border-gray-300 hover:bg-gray-50 hover:text-gray-900">
        <Pencil className="h-4 w-4" />
      </button>

      <button type="button" onClick={onClick} className="flex min-h-0 flex-1 flex-col text-left">
        <div className="flex items-start justify-between gap-2 pr-8">
          <h3 className="text-base font-bold leading-snug text-gray-900">{name}</h3>
          <TemplateApprovalBadge status={templateApprovalStatus} />
        </div>
        <p className="mt-3 flex-1 line-clamp-4 text-sm leading-relaxed text-gray-500">
          {description?.trim() ? description : 'No description'}
        </p>
        <div className="mt-5">
          <span className="text-sm text-gray-500">
            {stepCount} step{stepCount === 1 ? '' : 's'}
          </span>
        </div>
      </button>
    </div>
  );
}

const WhatsAppFlow = () => {
  const navigate = useNavigate();
  const userId = Cookies.get('userId');
  const queryClient = useQueryClient();
  const { collapsed } = useSidebar();
  const { projects, projectsForDropdown, isProjectsLoading } = useProjectsList();
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editingFlow, setEditingFlow] = useState(null);

  const {
    register,
    watch,
    setValue,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(projectFormSchema),
    defaultValues: { project: '' }
  });

  const {
    register: registerCreate,
    handleSubmit: handleCreateSubmit,
    reset: resetCreateForm,
    setValue: setCreateValue,
    watch: watchCreate,
    formState: { errors: createErrors }
  } = useForm({
    resolver: yupResolver(createFlowSchema),
    defaultValues: { name: '', description: '', vertical: '' }
  });

  const createVertical = watchCreate('vertical');

  const projectID = watch('project');

  const {
    data: flows = [],
    isLoading: isFlowsLoading,
    isError: isFlowsError
  } = useQuery({
    queryKey: ['whatsappFlows', projectID],
    queryFn: () => getWhatsAppFlows(projectID),
    enabled: Boolean(projectID)
  });

  const closeCreateModal = useCallback(() => {
    setCreateModalOpen(false);
    resetCreateForm();
  }, [resetCreateForm]);

  const createFlowMutation = useMutation({
    mutationFn: createWhatsAppFlow,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['whatsappFlows', projectID] });
      toast.success('WhatsApp flow created successfully.');
      setCreateModalOpen(false);
      resetCreateForm();
    },
    onError: (err) => {
      const msg = err?.response?.data?.message ?? err?.message;
      toast.error(typeof msg === 'string' ? msg : 'Could not create WhatsApp flow.');
    }
  });

  const syncTemplateStatusMutation = useMutation({
    mutationFn: () => syncWhatsAppFlowTemplateStatus(projectID),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['whatsappFlows', projectID] });
      // Sync rewrites step 0 (approval status, template name, category), so the per-flow
      // editor cache is stale too. The app sets a 100 minute global staleTime, so without
      // this the editor keeps serving the pre-sync copy until the cache expires.
      queryClient.invalidateQueries({ queryKey: ['whatsappFlow'] });

      // A silent UTILITY -> MARKETING move by Meta changes pricing and per-user send
      // limits, so call it out rather than folding it into the generic success toast.
      const recategorized = (data?.updated || []).filter(
        (entry) => entry?.new_category && entry.new_category !== entry.old_category
      );
      if (recategorized.length) {
        const summary = recategorized
          .map((entry) => `${entry.flow_name}: ${entry.old_category || '—'} → ${entry.new_category}`)
          .join(', ');
        toast.success(`Template statuses synced. Meta re-categorized ${summary}.`, {
          duration: 8000
        });
        return;
      }
      toast.success('Template statuses synced.');
    },
    onError: (err) => {
      const msg = err?.response?.data?.message ?? err?.message;
      toast.error(typeof msg === 'string' ? msg : 'Could not sync template status.');
    }
  });

  const onCreateFlowSubmit = useCallback(
    (data) => {
      const projectNumericId = Number.parseInt(String(projectID), 10);
      if (!Number.isFinite(projectNumericId)) {
        toast.error('Select a project first.');
        return;
      }

      const payload = {
        project_id: projectNumericId,
        name: data.name.trim(),
        slug: slugifyFlowName(data.name),
        description: data.description?.trim() || '',
        vertical: data.vertical.trim()
      };

      mixpanel.track('WhatsApp Flow Create Started', {
        user_id: userId,
        project_id: projectID,
        flow_name: payload.name,
        slug: payload.slug,
        timestamp: new Date().toISOString()
      });

      createFlowMutation.mutate(payload);
    },
    [createFlowMutation, projectID, userId]
  );

  useEffect(() => {
    mixpanel.track('WhatsApp Flow Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  useEffect(() => {
    if (projects?.length > 0 && !projectID) {
      setValue('project', projects[0].id);
    }
  }, [projects, projectID, setValue]);

  const visibleFlows = useMemo(() => {
    const sorted = [...flows].sort(
      (a, b) => new Date(b.updated_at || 0).getTime() - new Date(a.updated_at || 0).getTime()
    );
    return sorted.map(mapFlowRow);
  }, [flows]);

  const selectedProjectName = useMemo(() => {
    const project = projects?.find((p) => String(p.id) === String(projectID));
    return project?.name || 'this project';
  }, [projects, projectID]);

  const isLoading = isProjectsLoading || isFlowsLoading;

  const hasNoFlows = !isLoading && Boolean(projectID) && !isFlowsError && visibleFlows.length === 0;

  return (
    <>
      <Loader isVisible={isLoading || createFlowMutation.isPending || syncTemplateStatusMutation.isPending} />
      <Layout>
        <div
          className={`fixed z-[10] top-0 transition-all duration-300 ease-in-out ${
            collapsed ? 'left-[76px] right-0' : 'left-64 right-0'
          }`}>
          <Header
            backArrow={false}
            data={{
              name: 'WhatsApp Flow',
              subtitle: 'Create and manage WhatsApp question flows for your projects'
            }}
          />
        </div>

        <div
          className={`fixed z-[9] border-b border-stone-200/80 bg-white px-6 py-3 transition-all duration-300 ease-in-out ${
            collapsed ? 'left-[76px] right-0' : 'left-64 right-0'
          }`}
          style={{ top: FLOW_LIST_HEADER_OFFSET }}>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex min-w-0 max-w-md flex-1 items-center">
              <FormInput
                width={100}
                label=""
                register={register}
                register_key="project"
                isRequired
                type="dropdown"
                options={[
                  { label: 'Select Project', value: '', disabled: true },
                  ...(projectsForDropdown || [])
                ]}
                errors={errors}
                hideAstrik
              />
            </div>

            <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-stone-200 bg-stone-50 px-3 py-1 text-sm text-gray-600">
              <Workflow className="h-3.5 w-3.5 text-primary" strokeWidth={2} />
              <span className="font-semibold tabular-nums text-gray-900">
                {visibleFlows.length}
              </span>
              <span>flow{visibleFlows.length === 1 ? '' : 's'}</span>
            </span>

            <button
              type="button"
              onClick={() => syncTemplateStatusMutation.mutate()}
              disabled={!projectID || syncTemplateStatusMutation.isPending}
              className="ml-auto inline-flex shrink-0 items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-800 shadow-sm transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60">
              <RefreshCw
                className={`h-4 w-4 ${syncTemplateStatusMutation.isPending ? 'animate-spin' : ''}`}
                strokeWidth={2}
              />
              {syncTemplateStatusMutation.isPending ? 'Syncing…' : 'Sync template status'}
            </button>
          </div>
        </div>

        <div
          className="min-h-screen bg-[#F8F9FA] pb-10"
          style={{ marginTop: FLOW_LIST_TOP_CHROME }}>
          <div className="px-6 pt-6">
              {!isProjectsLoading && !projects?.length ? (
                <div className="rounded-xl border border-dashed border-gray-300 bg-white/60 py-16 text-center text-gray-500 text-sm">
                  No projects available. Add a project to manage WhatsApp flows.
                </div>
              ) : isFlowsError ? (
                <div className="rounded-xl border border-rose-200 bg-rose-50 py-16 text-center text-rose-700 text-sm">
                  Could not load WhatsApp flows. Please try again.
                </div>
              ) : hasNoFlows ? (
                <div className="rounded-2xl border border-dashed border-stone-300 bg-white px-6 py-16 sm:py-20 text-center shadow-sm">
                  <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
                    <Workflow className="h-7 w-7 text-primary" strokeWidth={2} />
                  </div>
                  <h3 className="mt-5 text-lg font-semibold text-gray-900">No flows available</h3>
                  <p className="mt-2 text-sm text-gray-500 max-w-md mx-auto leading-relaxed">
                    There are no WhatsApp flows for{' '}
                    <span className="font-medium text-gray-700">{selectedProjectName}</span> yet.
                    Create your first flow to start building automated question journeys for leads.
                  </p>
                  <button
                    type="button"
                    onClick={() => setCreateModalOpen(true)}
                    className="mt-6 inline-flex items-center gap-2 rounded-xl bg-primary px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-primary/90 transition-colors">
                    <Plus className="h-4 w-4" strokeWidth={2.5} />
                    Create new flow
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  <button
                    type="button"
                    onClick={() => setCreateModalOpen(true)}
                    disabled={!projectID}
                    className="rounded-2xl border-2 border-dashed border-stone-300 bg-white min-h-[200px] flex flex-col items-center justify-center gap-3 p-6 hover:border-primary/40 hover:bg-primary/5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                      <Plus className="w-6 h-6 text-primary" strokeWidth={2.5} />
                    </div>
                    <span className="text-sm font-semibold text-primary">Create new flow</span>
                  </button>

                  {visibleFlows.map((flow) => (
                    <FlowCard
                      key={flow.id}
                      name={flow.name}
                      description={flow.description}
                      stepCount={flow.stepCount}
                      templateApprovalStatus={flow.templateApprovalStatus}
                      onClick={() =>
                        navigate(`/builder/marketing/whatsapp-flow/${flow.id}`, {
                          state: { flowName: flow.name, flowDescription: flow.description }
                        })
                      }
                      onEdit={() => setEditingFlow(flow)}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
      </Layout>

      {createModalOpen ? (
        <Modal name="Name your flow" width={38} hideClose onClose={closeCreateModal}>
          <p className="-mt-2 mb-5 text-sm text-gray-500">
            Give this question flow a name your team will recognise
          </p>
          <form onSubmit={handleCreateSubmit(onCreateFlowSubmit)} className="space-y-4">
            <div>
              <label
                htmlFor="wa-flow-name"
                className="block text-sm font-medium text-gray-800 mb-1.5">
                Flow name <span className="text-red-500">*</span>
              </label>
              <input
                id="wa-flow-name"
                type="text"
                placeholder="Site Visit Confirmation"
                className="w-full rounded-lg border border-stone-300 px-3 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                {...registerCreate('name')}
              />
              {createErrors.name ? (
                <p className="mt-1 text-xs text-red-600">{createErrors.name.message}</p>
              ) : null}
            </div>
            <div>
              <label
                htmlFor="wa-flow-description"
                className="block text-sm font-medium text-gray-800 mb-1.5">
                Description (Optional)
              </label>
              <input
                id="wa-flow-description"
                type="text"
                placeholder="Type here.."
                className="w-full rounded-lg border border-stone-300 px-3 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                {...registerCreate('description')}
              />
            </div>
            <div>
              <label
                htmlFor="wa-flow-vertical"
                className="block text-sm font-medium text-gray-800 mb-1.5">
                Vertical <span className="text-red-500">*</span>
              </label>
              <select
                id="wa-flow-vertical"
                value={createVertical}
                onChange={(event) =>
                  setCreateValue('vertical', event.target.value, { shouldValidate: true })
                }
                className="w-full rounded-lg border border-stone-300 px-3 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary">
                <option value="" disabled>
                  Select vertical
                </option>
                {WHATSAPP_FLOW_VERTICAL_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {createErrors.vertical ? (
                <p className="mt-1 text-xs text-red-600">{createErrors.vertical.message}</p>
              ) : null}
            </div>
            <div className="pt-2 flex justify-end gap-3">
              <Button
                type="button"
                displayType="secondary"
                onClick={closeCreateModal}
                disabled={createFlowMutation.isPending}>
                Cancel
              </Button>
              <Button type="submit" disabled={createFlowMutation.isPending}>
                {createFlowMutation.isPending ? 'Creating…' : 'Continue'}
              </Button>
            </div>
          </form>
        </Modal>
      ) : null}

      {editingFlow ? (
        <WhatsAppFlowMetadataModal
          flowId={editingFlow.id}
          projectId={editingFlow.projectId ?? projectID}
          isOpen={Boolean(editingFlow)}
          onClose={() => setEditingFlow(null)}
          initialName={editingFlow.name}
          initialDescription={editingFlow.description || ''}
          initialVertical={editingFlow.vertical || ''}
        />
      ) : null}
    </>
  );
};

export default WhatsAppFlow;
